import { useQuery } from 'react-query'

import { useLogger } from '@vp/ubik-context'

import type { ColorOptionProduct } from '../../types/ColorOptionProduct'
import { buildColorOptionProducts, getAvailableColorsFromInventory, getColorKey } from '../../components/quantity/colorOption/colorOptionHelpers'
import { STALE_TIME_INFINITY } from './constants'
import { useAppState } from '../../components/root/contexts/AppContext'
import { getAvailabilityByFacet } from '../../clients/MerchantAvailabilityClient'
import { useAuthorization } from '../../components/root/contexts/AuthorizationProvider'
import { useTenant } from '../../components/root/contexts/TenantContext'
import type { MsxV2PropertyValue } from '../../types/MsxProperty'
import { getAttributesAndValues } from '../../clients/MsxClient'
import { EXPERIMENTS, useABTest } from '../../components/root/contexts/ABTestContext'

export default function useAvailableColorOptions() {
  const { primaryProject, culture, country } = useAppState()
  const { authorizationHeader } = useAuthorization()
  const { tenant, merchantId } = useTenant()
  const { currentExperiments, isAvailable } = useABTest()
  const logger = useLogger()

  const isControlVariant =
    EXPERIMENTS['ADD_OTHER_COLORS'] && EXPERIMENTS['ADD_OTHER_COLORS'].variations['TEST'] !== currentExperiments[EXPERIMENTS['ADD_OTHER_COLORS'].key]

  return useQuery<ColorOptionProduct[]>(
    ['available-color-options', primaryProject?.productKey, isControlVariant],
    async () => {
      if (!primaryProject || primaryProject.pageMode !== 'Sized Good' || isControlVariant) {
        return []
      }
      const colorKey = getColorKey(primaryProject?.lowestPriceAttributes)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [colorKey]: colorValue, Size, ...selectedAttributes } = primaryProject?.lowestPriceAttributes || {}

      const inventoryByColor = await getAvailabilityByFacet(
        merchantId,
        primaryProject?.productKey as string,
        primaryProject?.productVersion as number,
        country,
        selectedAttributes,
        colorKey,
        authorizationHeader!,
        logger,
      )

      const availableColors = colorValue ? getAvailableColorsFromInventory(inventoryByColor, colorKey, colorValue) : []

      let colorNames: MsxV2PropertyValue[] = []
      if (availableColors.length > 0) {
        const attributesAndValues = await getAttributesAndValues(
          tenant,
          primaryProject?.productKey as string,
          primaryProject?.productVersion as number,
          culture,
          logger,
        )

        const colorAttribute = attributesAndValues.find((attribute) => attribute.key.key === colorKey)
        if (colorAttribute?.values) {
          colorNames = [...colorAttribute.values]
        }
      }

      return buildColorOptionProducts(availableColors, colorNames, primaryProject, colorKey)
    },
    {
      enabled: !!primaryProject && isAvailable && !!authorizationHeader,
      staleTime: STALE_TIME_INFINITY,
    },
  )
}
